import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

/**
 * CURRENCY CONTEXT — INR-Base System
 * 
 * All product prices in the database are stored in INR (source of truth).
 * This context provides display-only conversion to USD.
 * Payment always happens in INR regardless of display currency.
 */
export const CurrencyProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [currency, setCurrency] = useState('USD'); // Default display: USD
    const [exchangeRate, setExchangeRate] = useState(83.5); // Fallback: 1 USD = 83.5 INR
    const [loading, setLoading] = useState(true);

    const API = import.meta.env.VITE_API_URL || 'https://octoink-backend.onrender.com';

    // Fetch exchange rate from our backend (cached, no CORS issues)
    useEffect(() => {
        const fetchRate = async () => {
            try {
                // Try our backend endpoint first (server-side cached)
                const response = await fetch(`${API}/api/stats/exchange-rate`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.rate) {
                        setExchangeRate(data.rate);
                        return;
                    }
                }
                // Fallback to direct API call
                const fallbackRes = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    if (fallbackData?.rates?.INR) {
                        setExchangeRate(fallbackData.rates.INR);
                    }
                }
            } catch (error) {
                console.error('[Currency] Failed to fetch exchange rate:', error);
                // Keep fallback rate of 83.5
            }
        };
        fetchRate();
    }, [API]);

    // Set initial currency based on user preference or geo-location
    useEffect(() => {
        const detectCurrency = async () => {
            // 1. Check localStorage preference first (works for guests too)
            const savedCurrency = localStorage.getItem('displayCurrency');
            if (savedCurrency && (savedCurrency === 'USD' || savedCurrency === 'INR')) {
                setCurrency(savedCurrency);
                setLoading(false);
                return;
            }

            // 2. Check user preference if logged in
            if (user && user.currency) {
                setCurrency(user.currency);
                localStorage.setItem('displayCurrency', user.currency);
                setLoading(false);
                return;
            }

            // 3. Geo-detection with Locale fallback
            try {
                const geoRes = await fetch('https://ipapi.co/json/');
                if (geoRes.ok) {
                    const geoData = await geoRes.json();
                    const detectedCurrency = geoData.country === 'IN' ? 'INR' : 'USD';
                    setCurrency(detectedCurrency);
                    localStorage.setItem('displayCurrency', detectedCurrency);
                } else {
                    throw new Error("GeoAPI failed");
                }
            } catch (geoErr) {
                console.warn('[Currency] Geo-detection failed, using browser locale fallback');
                const locale = Intl.NumberFormat().resolvedOptions().locale;
                const detectedCurrency = locale.includes('IN') ? 'INR' : 'USD';
                setCurrency(detectedCurrency);
                localStorage.setItem('displayCurrency', detectedCurrency);
            }
            setLoading(false);
        };

        detectCurrency();
    }, [user]);

    /**
     * Format a price stored in INR for display.
     * If display currency is USD, converts INR → USD.
     * If display currency is INR, shows as-is.
     * 
     * @param {number} priceINR - The price in INR from the database
     * @returns {string} - Formatted price string with currency symbol
     */
    const formatPrice = (priceINR) => {
        const amount = Number(priceINR) || 0;
        if (currency === 'USD') {
            const converted = (amount / exchangeRate).toFixed(2);
            return `$${converted}`;
        }
        return `₹${amount.toFixed(2)}`;
    };

    /**
     * Get the converted numeric amount for display.
     * @param {number} priceINR - The price in INR from the database
     * @returns {number} - Converted amount in display currency
     */
    const getConvertedAmount = (priceINR) => {
        const amount = Number(priceINR) || 0;
        if (currency === 'USD') {
            return Number((amount / exchangeRate).toFixed(2));
        }
        return amount;
    };

    /**
     * Toggle display currency. Saves to localStorage and user profile.
     * This is DISPLAY ONLY — does NOT affect payment currency (always INR).
     */
    const toggleCurrency = async (newCurrency) => {
        setCurrency(newCurrency);
        localStorage.setItem('displayCurrency', newCurrency);
        
        if (token) {
            try {
                await fetch(`${API}/api/auth/profile`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ currency: newCurrency })
                });
                
                const savedUserStr = localStorage.getItem('user');
                if (savedUserStr) {
                    try {
                        const savedUser = JSON.parse(savedUserStr);
                        if (savedUser) {
                            savedUser.currency = newCurrency;
                            localStorage.setItem('user', JSON.stringify(savedUser));
                        }
                    } catch (e) {
                        console.error("Failed to update user currency in localStorage:", e);
                    }
                }
            } catch (err) {
                console.error('[Currency] Failed to save currency preference:', err);
            }
        }
    };

    return (
        <CurrencyContext.Provider value={{ 
            currency, 
            exchangeRate, 
            formatPrice, 
            getConvertedAmount,
            toggleCurrency,
            loading 
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};
